import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AgentThread } from '@dreamai/types';
import { useAgent } from '../../../contexts/AgentContext';
import { useThreadReads } from '../../../providers/ThreadReadsProvider';
import api from '../../../api';
import { ReactComponent as TeamChatIcon } from '../../../icons/team-chat.svg';
import { ReactComponent as SendIcon } from '../../../icons/send.svg';
import { ReactComponent as CampaignIcon } from '../../../icons/campaign.svg';
import { ReactComponent as ClipboardIcon } from '../../../icons/clipboard-list.svg';
import { ReactComponent as MentionAtIcon } from '../../../icons/mention-at.svg';
import './side-nav-section.scss';

interface GetTeamChatsResponse {
  success: boolean;
  threads: AgentThread[];
  error?: string;
}

export interface TeamChatsSectionRef {
  refreshTeamChats: () => void;
}

const MAX_VISIBLE_THREADS = 5;

const TeamChatsSection = forwardRef<TeamChatsSectionRef, object>((_, ref) => {
  const { getAccessTokenSilently } = useAuth0();
  const { getAgentById } = useAgent();
  const [searchParams] = useSearchParams();
  const { getUnreadThreadIdsByAgent } = useThreadReads();

  const currentThreadId = searchParams.get('thread_id');

  const [threads, setThreads] = useState<AgentThread[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const fetchTeamChats = async () => {
    try {
      // Don't set loading on refresh to avoid flicker
      const token = await getAccessTokenSilently();
      const response = await api.get<GetTeamChatsResponse>(
        '/agents/threads/team',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        setThreads(response.data.threads);
      }
    } catch (err) {
      console.error('Error fetching team chats:', err);
    } finally {
      setInitialLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    refreshTeamChats: fetchTeamChats,
  }));

  useEffect(() => {
    fetchTeamChats();
  }, [getAccessTokenSilently]);

  // Filter out background/internal/learning/archived threads
  const visibleThreads = useMemo(
    () =>
      threads.filter(
        (t) =>
          !t.is_archived_thread &&
          !t.is_background &&
          !t.is_internal_monologue &&
          !t.is_learning_thread,
      ),
    [threads],
  );

  // Get all unread thread IDs across all agents
  const unreadThreadIds = useMemo(() => {
    const allIds = new Set<string>();
    // Check each thread's agent for unread status
    visibleThreads.forEach((t) => {
      if (t.agent_id) {
        const agentUnreads = getUnreadThreadIdsByAgent(t.agent_id);
        if (agentUnreads.includes(t.id)) {
          allIds.add(t.id);
        }
      }
    });
    return allIds;
  }, [visibleThreads, getUnreadThreadIdsByAgent]);

  // Helper to check if thread has priority (mentions, actions, or unread)
  const hasPriority = (t: AgentThread) =>
    t.has_unread_mentions ||
    t.has_pending_confirmations ||
    unreadThreadIds.has(t.id);

  // Helper to check if thread should be visible (has priority or recent activity)
  const isVisibleThread = useMemo(() => {
    const fiveDaysAgo = new Date();
    fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);

    return (t: AgentThread) => {
      if (hasPriority(t)) return true;
      const lastUpdated = new Date(t.last_updated_at);
      return lastUpdated >= fiveDaysAgo;
    };
  }, [unreadThreadIds]);

  // Sort by priority: mentions > confirmations > unread > others
  const sortedThreads = useMemo(() => {
    return [...visibleThreads].sort((a, b) => {
      if (a.has_unread_mentions && !b.has_unread_mentions) return -1;
      if (!a.has_unread_mentions && b.has_unread_mentions) return 1;

      if (a.has_pending_confirmations && !b.has_pending_confirmations)
        return -1;
      if (!a.has_pending_confirmations && b.has_pending_confirmations) return 1;

      const aUnread = unreadThreadIds.has(a.id);
      const bUnread = unreadThreadIds.has(b.id);
      if (aUnread && !bUnread) return -1;
      if (!aUnread && bUnread) return 1;

      return 0;
    });
  }, [visibleThreads, unreadThreadIds]);

  // Filter for display (eligible = priority OR recent activity)
  const eligibleThreads = sortedThreads.filter(isVisibleThread);
  // Always show at least one thread if there are any, even if none pass the filter
  const displayedThreads = isExpanded
    ? sortedThreads
    : eligibleThreads.length > 0
      ? eligibleThreads.slice(0, MAX_VISIBLE_THREADS)
      : sortedThreads.slice(0, 1);

  // Hidden threads for "+ X more"
  const displayedIds = new Set(displayedThreads.map((t) => t.id));
  const hiddenThreads = sortedThreads.filter((t) => !displayedIds.has(t.id));
  const hasHiddenThreads = !isExpanded && hiddenThreads.length > 0;

  // Calculate hidden thread stats
  const hiddenStats = useMemo(() => {
    if (!hasHiddenThreads) return null;

    const hiddenCount = hiddenThreads.length;
    const hiddenMentions = hiddenThreads.filter(
      (t) => t.has_unread_mentions,
    ).length;
    const hiddenActions = hiddenThreads.filter(
      (t) => t.has_pending_confirmations,
    ).length;
    const hiddenUnread = hiddenThreads.filter((t) =>
      unreadThreadIds.has(t.id),
    ).length;

    return { hiddenCount, hiddenMentions, hiddenActions, hiddenUnread };
  }, [hasHiddenThreads, hiddenThreads, unreadThreadIds]);

  const getThreadIcon = (thread: AgentThread) => {
    let icon = <SendIcon />;
    if (
      thread.discussing_objects?.some((d) => d.discussing_object === 'Campaign')
    ) {
      icon = <CampaignIcon />;
    } else if (
      thread.discussing_objects?.some((d) => d.discussing_object === 'Plan')
    ) {
      icon = <ClipboardIcon />;
    }

    if (thread.has_unread_mentions) {
      return <MentionAtIcon />;
    }

    if (thread.has_pending_confirmations) {
      return (
        <div className='thread-icon-wrapper'>
          {icon}
          <div className='action-required-indicator'>
            <div className='action-required-indicator-dot' />
          </div>
        </div>
      );
    }

    return icon;
  };

  const getThreadName = (thread: AgentThread) => {
    if (thread.is_main_thread) return 'Direct Message';
    return thread.description || 'Untitled';
  };

  const getThreadClasses = (thread: AgentThread) => {
    const classes = ['thread-item'];
    if (currentThreadId === thread.id) classes.push('active');
    if (unreadThreadIds.has(thread.id)) classes.push('has-unread');
    if (thread.has_pending_confirmations)
      classes.push('has-pending-confirmations');
    if (thread.has_unread_mentions) classes.push('has-unread-mentions');
    return classes.join(' ');
  };

  // Don't render if no team chats (after initial load)
  if (!initialLoading && visibleThreads.length === 0) {
    return null;
  }

  return (
    <div className='side-nav-section team-chats-section'>
      <div className='section-header'>
        <div className='agent-info'>
          <div className='team-chat-icon'>
            <TeamChatIcon />
          </div>
          <p className='nickname'>Team Chats</p>
        </div>
      </div>

      <div className='threads'>
        {displayedThreads.map((thread) => {
          return (
            <Link
              key={thread.id}
              to={`/conversations?agent_id=${thread.agent_id || ''}&thread_id=${thread.id}`}
              className={getThreadClasses(thread)}
            >
              {getThreadIcon(thread)}
              <span className='thread-name'>{getThreadName(thread)}</span>
            </Link>
          );
        })}

        {/* Expand button when collapsed */}
        {hasHiddenThreads && hiddenStats && (
          <button
            className='expand-threads-btn'
            onClick={() => setIsExpanded(true)}
          >
            + {hiddenStats.hiddenCount} more
            {(hiddenStats.hiddenMentions > 0 ||
              hiddenStats.hiddenActions > 0 ||
              hiddenStats.hiddenUnread > 0) && (
              <span className='hidden-stats'>
                (
                {[
                  hiddenStats.hiddenMentions > 0 &&
                    `${hiddenStats.hiddenMentions} mention${hiddenStats.hiddenMentions > 1 ? 's' : ''}`,
                  hiddenStats.hiddenActions > 0 &&
                    `${hiddenStats.hiddenActions} action${hiddenStats.hiddenActions > 1 ? 's' : ''}`,
                  hiddenStats.hiddenUnread > 0 &&
                    `${hiddenStats.hiddenUnread} unread`,
                ]
                  .filter(Boolean)
                  .join(', ')}
                )
              </span>
            )}
          </button>
        )}

        {/* Collapse button when expanded */}
        {isExpanded && sortedThreads.length > MAX_VISIBLE_THREADS && (
          <button
            className='expand-threads-btn'
            onClick={() => setIsExpanded(false)}
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
});

TeamChatsSection.displayName = 'TeamChatsSection';

export default TeamChatsSection;
